import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../context/GameContext.jsx";
import { useLanguage } from "../hooks/useLanguage.js";
import { fadeUp } from "../animations/variants.js";
import { Panel } from "../components/ui/Panel.jsx";
import { LangToggle } from "../components/ui/LangToggle.jsx";
import { ScrollNav } from "../components/ui/ScrollNav.jsx";
import { RoleBadge } from "../components/role/RoleBadge.jsx";
import { CatalogModal } from "../components/role/CatalogModal.jsx";
import { ROLE_DATA } from "../data/roles.js";
import { CATALOG_CATEGORIES } from "../data/constants.js";

function RuleSection({ title, children }) {
  return (
    <div className="mb-5">
      <h3 className="text-amber-300 font-bold text-sm mb-2 uppercase tracking-wider">{title}</h3>
      <div className="text-amber-100/80 text-sm leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export function DocsScreen() {
  const { dispatch } = useGame();
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("rules");
  const [catTab, setCatTab] = useState("village");
  const [catalogModal, setCatalogModal] = useState(null);
  const scrollRef = useRef(null);

  const tabs = [
    { id:"rules",   label: t("📜 Règles","📜 Rules") },
    { id:"catalog", label: t("🃏 Cartes","🃏 Cards") },
  ];
  const catTabs = [
    { id:"village",  label: t("Village","Village") },
    { id:"werewolf", label: t("Loups","Wolves") },
    { id:"solo",     label: t("Solo","Solo") },
    { id:"mixed",    label: t("Mixte","Mixed") },
  ];

  return (
    <motion.div variants={fadeUp} initial="initial" animate="animate" exit="exit"
      className="min-h-screen bg-gradient-to-b from-stone-950 via-amber-950 to-stone-950 flex flex-col">

      <div className="px-4 pt-10 pb-3 flex items-center gap-3">
        <button onClick={() => dispatch({ type: "GO_TO_HOME" })}
          className="text-amber-600 text-xl w-9 h-9 rounded-xl bg-amber-950/50 border border-amber-800/30 flex items-center justify-center active:scale-95">←</button>
        <div className="flex-1">
          <h1 className="text-amber-200 font-bold text-lg">{t("📖 Documentation","📖 Documentation")}</h1>
        </div>
        <LangToggle />
      </div>

      <div className="flex gap-2 px-4 mb-2">
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition-all active:scale-95
              ${tab === tb.id ? "bg-amber-600 text-amber-950" : "bg-amber-950/40 border border-amber-800/30 text-amber-500"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 px-4 overflow-y-auto pb-10">
        <AnimatePresence mode="wait">

          {tab === "rules" && (
            <motion.div key="rules" variants={fadeUp} initial="initial" animate="animate" exit="exit">
              <Panel className="mt-2">
                <RuleSection title={t("Objectif","Goal of the game")}>
                  <p>{t(
                    "Les Loups-Garous se cachent parmi les Villageois. Chaque nuit ils éliminent quelqu'un. Chaque jour le village débat et vote. La partie continue jusqu'à ce qu'un camp remporte la victoire.",
                    "Werewolves secretly hide among the Villagers. Each night they eliminate someone. Each day the village debates and votes. The game continues until one side fulfills its victory condition."
                  )}</p>
                </RuleSection>

                <RuleSection title={t("Cycle nuit / jour","Day and Night cycle")}>
                  <p className="font-semibold text-amber-300">{t("🌙 Nuit","🌙 Night")}</p>
                  <p>{t(
                    "Les rôles spéciaux agissent dans l'ordre. Les Loups choisissent une victime. Certains personnages peuvent protéger, enquêter ou manipuler les événements.",
                    "Special roles act in order. Werewolves choose a victim. Some characters may protect, investigate or manipulate events."
                  )}</p>
                  <p className="font-semibold text-amber-300 mt-2">{t("☀️ Jour","☀️ Day")}</p>
                  <p>{t(
                    "Tout le monde se réveille. Les morts sont annoncés. Les joueurs débattent. Le village vote. Le cycle recommence.",
                    "Everyone wakes up. Deaths are announced. Players debate. The village votes. The cycle repeats."
                  )}</p>
                </RuleSection>

                <RuleSection title={t("Mort","Death")}>
                  <p>{t(
                    "Les joueurs morts quittent la discussion et deviennent spectateurs. Ils ne peuvent plus influencer la partie.",
                    "Dead players leave the discussion and become spectators. They may no longer influence the game."
                  )}</p>
                </RuleSection>

                <RuleSection title={t("Maire","Mayor")}>
                  <p>{t(
                    "Si activé, le village élit un Maire. Le Maire décide qui prend la parole en premier. Selon les variantes, le Maire peut aussi départager les égalités.",
                    "If enabled, the village elects a Mayor. The Mayor decides who speaks first. Some variants also allow the Mayor to break ties."
                  )}</p>
                </RuleSection>

                <RuleSection title={t("Vote","Voting")}>
                  <p>{t(
                    "Les joueurs votent pour éliminer un suspect. En cas d'égalité selon les réglages : passer l'exécution, relancer le vote, ou laisser le Maire décider.",
                    "Players vote for someone to eliminate. In case of ties, depending on settings: skip the execution, restart voting, or let the Mayor decide."
                  )}</p>
                </RuleSection>

                <RuleSection title={t("Alignements","Alignments")}>
                  <p><span className="text-emerald-400 font-semibold">{t("Village :","Village:")}</span> {t("travaillent ensemble pour éliminer toutes les menaces.","work together to eliminate all threats.")}</p>
                  <p><span className="text-red-400 font-semibold">{t("Loups-Garous :","Werewolves:")}</span> {t("chassent secrètement la nuit.","secretly hunt at night.")}</p>
                  <p><span className="text-teal-400 font-semibold">{t("Solo :","Solo:")}</span> {t("ont leurs propres objectifs et peuvent trahir tout le monde.","have their own objectives and may betray everyone.")}</p>
                  <p><span className="text-amber-400 font-semibold">{t("Mixte :","Mixed:")}</span> {t("peuvent changer d'alignement en cours de partie.","can change alignment during the game.")}</p>
                </RuleSection>

                <RuleSection title={t("Animateur / Modérateur","Moderator")}>
                  <p>{t(
                    "Cette application est destinée à un Animateur. Il guide les joueurs, annonce les événements et saisit les décisions dans l'application. Les joueurs eux-mêmes n'interagissent pas avec l'app.",
                    "This app is intended for a Moderator. The Moderator guides players, announces events and enters decisions into the application. Players themselves do not interact with the app."
                  )}</p>
                </RuleSection>
              </Panel>
            </motion.div>
          )}

          {tab === "catalog" && (
            <motion.div key="catalog" variants={fadeUp} initial="initial" animate="animate" exit="exit">
              <div className="flex gap-1.5 mt-2 mb-3 flex-wrap">
                {catTabs.map(ct => (
                  <button key={ct.id} onClick={() => setCatTab(ct.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-95
                      ${catTab === ct.id ? "bg-amber-600 text-amber-950" : "bg-amber-950/40 border border-amber-800/30 text-amber-500"}`}>
                    {ct.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {(CATALOG_CATEGORIES[catTab] || []).map(roleId => {
                  const role = ROLE_DATA[roleId];
                  if (!role) return null;
                  return (
                    <motion.button key={roleId} whileTap={{ scale:0.95 }}
                      onClick={() => setCatalogModal(roleId)}
                      className={`relative rounded-2xl overflow-hidden border-2 ${role.border} bg-gradient-to-b ${role.gradient} flex flex-col`}>
                      <div className="w-full aspect-[3/4] bg-black/30 flex items-center justify-center relative">
                        {role.imageUrl
                          ? <img src={role.imageUrl} alt={role.englishName} className="w-full h-full object-cover" />
                          : <span className="text-4xl">{role.emoji}</span>}
                        {role.roleBadge && (
                          <div className="absolute top-1 left-1">
                            <RoleBadge roleBadge={role.roleBadge} lang={lang} />
                          </div>
                        )}
                      </div>
                      <div className="px-1.5 py-2 text-center">
                        <p className="text-white text-[11px] font-bold leading-tight">{lang === "fr" ? role.frenchName : role.englishName}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <ScrollNav scrollRef={scrollRef} />

      <AnimatePresence>
        {catalogModal && <CatalogModal key={catalogModal} roleId={catalogModal} onClose={() => setCatalogModal(null)} />}
      </AnimatePresence>
    </motion.div>
  );
}
